/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: components/VoiceGuidesColumns.tsx
 🗓️ Created: 2025-01-XX
 🧩 Description:
    Column definitions for Voice Guides DataGrid.
    Extracted from VoiceGuidesPage for better modularity.
────────────────────────────────────────────────────────────────*/

import { useMemo } from 'react';
import { Badge } from '../../../components/ui/badge';
import { SimpleTooltip } from '../../../components/ui/SimpleTooltip';
import { DataGridColumn } from '../../../hooks/useDataGrid';
import { VoiceGuide } from '../types';
import { formatDate, formatVoiceName, formatLanguageCode, formatDialectCode } from '../utils';
import { useI18n } from '../../../hooks/useI18n';

/**
 * Get column definitions for Voice Guides DataGrid
 */
export function useVoiceGuidesColumns(): DataGridColumn<VoiceGuide>[] {
  const { t } = useI18n();

  return useMemo(
    () => [
      {
        key: 'name',
        label: t('voiceGuides:columns.name') || 'Name',
        sortable: true,
        visible: true,
        render: (_value: unknown, item: VoiceGuide) => (
          <span className="font-medium text-primary">{item?.name || '-'}</span>
        ),
      },
      {
        key: 'language_code',
        label: t('voiceGuides:columns.language') || 'Language',
        sortable: true,
        visible: true,
        render: (_value: unknown, item: VoiceGuide) => (
          <Badge variant="secondary" className="text-xs font-medium">
            {formatLanguageCode(item?.language_code)}
          </Badge>
        ),
      },
      {
        key: 'dialect_code',
        label: t('voiceGuides:columns.dialect') || 'Dialect',
        sortable: true,
        visible: true,
        render: (_value: unknown, item: VoiceGuide) => (
          <Badge variant="outline" className="text-xs">
            {formatDialectCode(item?.dialect_code)}
          </Badge>
        ),
      },
      {
        key: 'voice_name',
        label: t('voiceGuides:columns.voice') || 'Voice',
        sortable: true,
        visible: true,
        render: (_value: unknown, item: VoiceGuide) => (
          <span className="text-sm font-medium text-foreground">
            {formatVoiceName(item?.voice_name)}
          </span>
        ),
      },
      {
        key: 'sound_path',
        label: t('voiceGuides:columns.path') || 'Path',
        sortable: true,
        visible: true,
        render: (_value: unknown, item: VoiceGuide) => (
          <SimpleTooltip content={item?.sound_path || '-'} position="top">
            <code className="block max-w-[260px] truncate text-xs font-mono text-muted-foreground">
              {item?.sound_path || '-'}
            </code>
          </SimpleTooltip>
        ),
      },
      {
        key: 'is_system',
        label: t('voiceGuides:columns.type') || 'Type',
        sortable: true,
        visible: true,
        render: (_value: unknown, item: VoiceGuide) => (
          <Badge
            variant={item?.is_system ? 'default' : 'outline'}
            className={
              item?.is_system
                ? 'bg-blue-500/15 text-blue-600 dark:text-blue-300 border-blue-500/30'
                : 'bg-emerald-500/15 text-emerald-600 dark:text-emerald-300 border-emerald-500/30'
            }
          >
            {item?.is_system
              ? t('voiceGuides.type.system') || 'System'
              : t('voiceGuides.type.custom') || 'Custom'}
          </Badge>
        ),
      },
      {
        key: 'enabled',
        label: t('voiceGuides:columns.status') || 'Status',
        sortable: true,
        visible: true,
        render: (_value: unknown, item: VoiceGuide) => (
          <Badge
            variant={item?.enabled ? 'default' : 'outline'}
            className={
              item?.enabled
                ? 'bg-emerald-500/15 text-emerald-600 dark:text-emerald-300'
                : 'text-muted-foreground'
            }
          >
            {item?.enabled
              ? t('voiceGuides.status.enabled') || 'Enabled'
              : t('voiceGuides.status.disabled') || 'Disabled'}
          </Badge>
        ),
      },
      {
        key: 'created_at',
        label: t('voiceGuides:columns.createdAt') || t('common:created') || 'Created',
        sortable: true,
        visible: false,
        render: (_value: unknown, item: VoiceGuide) =>
          formatDate((item as unknown as { createdAt?: string }).createdAt ?? item.created_at),
      },
      {
        key: 'actions',
        label: t('common:actions') || 'Actions',
        sortable: false,
        visible: true,
      },
    ],
    [t],
  );
}

